import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import PlatformBadge from '../common/PlatformBadge';
import type { ScheduledPostEntry } from '../../types/calendar';

type PlatformType = 'linkedin' | 'instagram' | 'facebook' | 'tiktok' | 'youtube' | 'twitter';

interface CalendarPostCardProps {
  /** The scheduled post entry to display */
  post: ScheduledPostEntry;
  /** Optional post title (from joined post data) */
  title?: string;
  /** Click handler for opening post details */
  onClick?: (post: ScheduledPostEntry) => void;
  /** Handler for reschedule action */
  onReschedule?: (post: ScheduledPostEntry) => void;
  /** Handler for unschedule action */
  onUnschedule?: (post: ScheduledPostEntry) => void;
  /** Whether to show compact view (for small cells) */
  compact?: boolean;
}

/**
 * CalendarPostCard component
 * Displays a scheduled post within a calendar cell with platform badges,
 * time display, and action menu for rescheduling/unscheduling.
 */
export default function CalendarPostCard({
  post,
  title,
  onClick,
  onReschedule,
  onUnschedule,
  compact = false,
}: CalendarPostCardProps) {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  /**
   * Format the scheduled time
   */
  const formatTime = (isoDate: string): string => {
    const date = new Date(isoDate);
    return format(date, 'HH:mm');
  };

  /**
   * Get status indicator color and label
   */
  const getStatusInfo = (): { color: string; label: string } => {
    const status = post.post.status;
    switch (status) {
      case 'scheduled':
        return { color: 'bg-blue-500', label: t('calendar.post.status.scheduled') };
      case 'published':
        return { color: 'bg-green-500', label: t('calendar.post.status.published') };
      case 'failed':
        return { color: 'bg-red-500', label: t('calendar.post.status.failed') };
      default:
        return { color: 'bg-gray-400', label: status };
    }
  };

  /**
   * Get platforms as typed array
   */
  const getPlatforms = (): PlatformType[] => {
    const validPlatforms: PlatformType[] = ['linkedin', 'instagram', 'facebook', 'tiktok', 'youtube', 'twitter'];
    return post.post.platforms.filter((p): p is PlatformType =>
      validPlatforms.includes(p as PlatformType)
    );
  };

  /**
   * Truncate title for display
   */
  const getTruncatedTitle = (): string => {
    const displayTitle = title || t('calendar.post.untitled');
    const maxLength = compact ? 20 : 40;
    return displayTitle.length > maxLength
      ? `${displayTitle.substring(0, maxLength)}...`
      : displayTitle;
  };

  /**
   * Handle card click
   */
  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on menu button
    if ((e.target as HTMLElement).closest('[data-menu-button]')) {
      return;
    }
    onClick?.(post);
  };

  /**
   * Handle context menu (right-click)
   */
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowMenu(true);
  };

  /**
   * Handle menu action
   */
  const handleMenuAction = (action: 'reschedule' | 'unschedule') => {
    setShowMenu(false);
    if (action === 'reschedule') {
      onReschedule?.(post);
    } else {
      onUnschedule?.(post);
    }
  };

  const statusInfo = getStatusInfo();
  const platforms = getPlatforms();

  // Compact view for small calendar cells
  if (compact) {
    return (
      <div
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e as unknown as React.MouseEvent);
          }
        }}
        role="button"
        tabIndex={0}
        className="
          w-full text-left p-1.5 rounded border border-black dark:border-gray-600
          bg-white dark:bg-gray-800
          hover:shadow-brutal
          transition-all duration-150 cursor-pointer
          relative group
        "
      >
        {/* Platform indicator dots */}
        <div className="flex items-center gap-1 mb-1">
          {platforms.slice(0, 3).map((platform) => (
            <span
              key={platform}
              className={`w-2 h-2 rounded-full ${
                platform === 'linkedin' ? 'bg-[#0077b5]' :
                platform === 'instagram' ? 'bg-[#E1306C]' :
                platform === 'facebook' ? 'bg-[#1877f2]' :
                platform === 'tiktok' ? 'bg-black dark:bg-white' :
                platform === 'youtube' ? 'bg-[#FF0000]' :
                platform === 'twitter' ? 'bg-[#1da1f2]' : 'bg-gray-400'
              }`}
              title={platform}
            />
          ))}
          {platforms.length > 3 && (
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              +{platforms.length - 3}
            </span>
          )}
          <span className="ml-auto text-[10px] text-gray-500 dark:text-gray-400 font-medium">
            {formatTime(post.scheduledAt)}
          </span>
        </div>

        {/* Title */}
        <p className="text-xs text-gray-900 dark:text-gray-100 font-medium truncate">
          {getTruncatedTitle()}
        </p>

        {/* Conflict warning indicator */}
        {post.conflictWarning && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full border border-black" title={t('calendar.post.conflict')} />
        )}
      </div>
    );
  }

  // Full view for larger cells
  return (
    <div className="relative" ref={menuRef}>
      <div
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e as unknown as React.MouseEvent);
          }
        }}
        role="button"
        tabIndex={0}
        className="
          w-full text-left p-3 rounded-lg border-2 border-black dark:border-gray-600
          bg-white dark:bg-gray-800
          hover:shadow-brutal
          transition-all duration-150 cursor-pointer
          relative group
        "
      >
        {/* Header: Time and Status */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
            {formatTime(post.scheduledAt)}
          </span>
          <div className="flex items-center gap-2">
            {/* Auto-publish indicator */}
            {post.autoPublish && (
              <span
                className="text-xs text-gray-500 dark:text-gray-400"
                title={t('calendar.post.autoPublish')}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>
            )}
            {/* Status indicator */}
            <span
              className={`w-2 h-2 rounded-full ${statusInfo.color}`}
              title={statusInfo.label}
            />
            {/* Menu button */}
            <button
              data-menu-button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="
                opacity-0 group-hover:opacity-100
                w-6 h-6 flex items-center justify-center rounded
                hover:bg-gray-100 dark:hover:bg-gray-700
                transition-all
              "
              aria-label={t('calendar.post.menu')}
            >
              <svg
                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Title */}
        <p className="text-sm text-gray-900 dark:text-gray-100 font-medium mb-2">
          {getTruncatedTitle()}
        </p>

        {/* Platform Badges */}
        <div className="flex flex-wrap gap-1">
          {platforms.map((platform) => (
            <PlatformBadge key={platform} platform={platform} className="text-[10px] px-1.5 py-0.5" />
          ))}
        </div>

        {/* Recurring indicator */}
        {post.isRecurring && (
          <div className="mt-2 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>{t('calendar.post.recurring')}</span>
          </div>
        )}

        {/* Conflict warning */}
        {post.conflictWarning && (
          <div className="mt-2 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span>{t('calendar.post.conflict')}</span>
          </div>
        )}
      </div>

      {/* Context Menu */}
      {showMenu && (
        <div className="
          absolute right-0 top-full mt-1 z-50
          bg-white dark:bg-gray-800
          border-2 border-black dark:border-gray-600
          rounded-lg shadow-brutal
          py-1 min-w-[140px]
        ">
          <button
            onClick={() => handleMenuAction('reschedule')}
            className="
              w-full px-3 py-2 text-left text-sm
              text-gray-900 dark:text-gray-100
              hover:bg-gray-100 dark:hover:bg-gray-700
              flex items-center gap-2
            "
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {t('calendar.post.actions.reschedule')}
          </button>
          <button
            onClick={() => handleMenuAction('unschedule')}
            className="
              w-full px-3 py-2 text-left text-sm
              text-red-600 dark:text-red-400
              hover:bg-red-50 dark:hover:bg-red-900/20
              flex items-center gap-2
            "
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            {t('calendar.post.actions.unschedule')}
          </button>
        </div>
      )}
    </div>
  );
}
